import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { MainHead } from '../../../components/common/head/MainHead';
import { Icon } from '../../../components/common/icon/Icon';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { useGetDictionaryCategoriesQuery } from '../../../hooks/api/dictionary/queries/useGetDictionaryCategories.query';
import { useLayout } from '../../../hooks/screen/useLayout';
import { IDictionaryCategory } from '../../../interfaces/Dictionary.interfaces';
import { DictionarySearchSection } from './components/DictionarySearchSection';

export const DictionaryMainPage = () => {
  const navigate = useNavigate();

  useLayout({
    fixedHeader: <MainHead backButton={{ isShow: true }} />,
    fixedHeaderHeight: 44,
    fixedFooterHeight: 92,
  });

  const { data: dictionaryCategories, isLoading: isGetDictionaryCategoriesQueryLoading } =
    useGetDictionaryCategoriesQuery();

  const handleTagClick = ({ tagId }: { tagId: string }) => {
    navigate(`/dictionary/machines?tagId=${tagId}`);
  };

  const sortedCategories = useMemo<IDictionaryCategory[]>(() => {
    if (dictionaryCategories) {
      return (
        // category sorted by weight
        dictionaryCategories
          .sort((a, b) => a.weight - b.weight)
          .map((category) => {
            const newCategory = category;

            // tags sorted by order
            newCategory.tags = category.tags.sort((a, b) => a.order - b.order);
            return newCategory;
          })
      );
    }

    return [];
  }, [dictionaryCategories]);

  if (isGetDictionaryCategoriesQueryLoading) return <AnimateLoading />;

  if (sortedCategories && sortedCategories.length > 0) {
    return (
      <>
        <Container>
          <DictionaryHeader>
            <Icon name={'Book'} width={48} height={48} />
            <DescriptionContianer>
              <DescriptionDefault>{'트랙터 정보를 쉽게, 한눈에.'}</DescriptionDefault>
              <DescriptionBold>{'플루바 트랙터 백서'}</DescriptionBold>
            </DescriptionContianer>
          </DictionaryHeader>

          <CategorySection>
            {sortedCategories &&
              sortedCategories.map((category) => (
                <CategoryWrap key={category.id}>
                  <CategoryHeaderContainer>
                    <CategoryInfo>
                      <CategoryTitle>{category.title}</CategoryTitle>
                      <CategoryDescription>{category.subtitle}</CategoryDescription>
                    </CategoryInfo>
                    {category.title.includes('브랜드 분류') && (
                      <CategoryMorePage onClick={() => navigate('/dictionary/brands')}>
                        <CategoryMorePageText>{'전체보기'}</CategoryMorePageText>
                        <svg
                          xmlns={'http://www.w3.org/2000/svg'}
                          width={'10'}
                          height={'10'}
                          viewBox={'0 0 10 10'}
                          fill={'none'}
                        >
                          <path
                            d={'M3 1.5L7 5L3 8.5'}
                            stroke={'#AEAFB6'}
                            strokeMiterlimit={'10'}
                            strokeLinecap={'round'}
                            strokeLinejoin={'round'}
                          />
                        </svg>
                      </CategoryMorePage>
                    )}
                  </CategoryHeaderContainer>

                  {category.componentType === 'LARGE' && (
                    <TagContainerSlide>
                      {category.tags.map((tag) => (
                        <TagLarge
                          onClick={() =>
                            handleTagClick({
                              tagId: tag.id,
                            })
                          }
                        >
                          <TagLargeTitle>{tag.title}</TagLargeTitle>
                        </TagLarge>
                      ))}
                    </TagContainerSlide>
                  )}

                  {category.componentType === 'MEDIUM' && (
                    <TagContainerSlide>
                      {category.tags.map((tag) => (
                        <TagMedium
                          onClick={() =>
                            handleTagClick({
                              tagId: tag.id,
                            })
                          }
                        >
                          <TagMediumType>{tag.title}</TagMediumType>
                          <TagMediumTitle>{tag.subtitle}</TagMediumTitle>
                          <TagMediumDescription>{tag.subtitle}</TagMediumDescription>
                        </TagMedium>
                      ))}
                    </TagContainerSlide>
                  )}

                  {category.componentType === 'SMALL' && (
                    <TagContainerGrid>
                      {category.tags.map((tag) => (
                        <TagSmall
                          onClick={() =>
                            handleTagClick({
                              tagId: tag.id,
                            })
                          }
                        >
                          <TagSmallTitle>{tag.title}</TagSmallTitle>
                        </TagSmall>
                      ))}
                    </TagContainerGrid>
                  )}
                </CategoryWrap>
              ))}
          </CategorySection>
        </Container>

        <DictionarySearchSection />
      </>
    );
  }

  return <></>;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  width: 100%;

  gap: 16px;
  padding: 16px;
`;

const DictionaryHeader = styled.div`
  width: 100%;

  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const DescriptionContianer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  gap: 4px;

  margin-left: 8px;
`;

const DescriptionDefault = styled.h2`
  color: #505363;
  font-size: 12px;
  line-height: 100%; /* 12px */
`;

const DescriptionBold = styled.h1`
  color: #232429;
  font-size: 18px;
  font-weight: 700;
  line-height: 100%; /* 18px */
`;

const CategorySection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  gap: 32px;

  width: 100%;
`;

const CategoryWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  width: 100%;
`;

const CategoryHeaderContainer = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CategoryInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const CategoryTitle = styled.h3`
  color: #445eff;
  font-weight: 500;
  line-height: 100%; /* 12px */

  margin-bottom: 4px;
`;

const CategoryDescription = styled.span`
  color: #232429;
  font-size: 14px;
  font-weight: 500;
  line-height: 100%; /* 14px */

  margin-bottom: 16px;
`;

const CategoryMorePage = styled.button`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const CategoryMorePageText = styled.span`
  color: #aeafb6;
  /* System/Small/s-medium */
  font-size: 12px;
  font-weight: 500;
  line-height: 100%; /* 12px */

  margin-right: 4px;
`;

const TagContainerSlide = styled.div`
  width: 100%;

  display: grid;
  grid-gap: 10px;
  grid-template-columns: repeat(auto-fill, 130px);
  grid-auto-flow: column;
  grid-auto-columns: 130px;
  overflow-x: auto;

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  ::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
`;

const TagContainerGrid = styled.div`
  width: 100%;

  display: grid;
  grid-template-columns: repeat(auto-fill, 80px);

  gap: 10px;
`;

const Tag = styled.div`
  padding: 16px;
  border-radius: 16px;

  cursor: pointer;
`;

const TagLarge = styled(Tag)`
  width: 130px;
  height: 130px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  background-color: #e5e9ff;
`;

const TagLargeTitle = styled.span`
  color: #445eff;
  font-size: 14px;
  font-weight: 500;
  line-height: 100%; /* 14px */
`;

const TagMedium = styled(Tag)`
  width: 130px;
  height: 90px;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  background-color: #f3f4f5;
`;

const TagMediumType = styled.span`
  color: #505363;
  font-size: 12px;
  line-height: 100%;

  margin-bottom: 2px;
`;

const TagMediumTitle = styled.span`
  color: #232429;
  font-weight: 700;
  line-height: 100%;

  margin-bottom: 16px;
`;

const TagMediumDescription = styled.span`
  color: #898b94;
  font-size: 12px;
  font-weight: 500;
  line-height: 100%;
`;

const TagSmall = styled(Tag)`
  height: 80px;
  max-width: 82px;

  display: flex;
  justify-content: center;
  align-items: center;

  background-color: #f3f4f5;
`;

const TagSmallTitle = styled.span`
  color: #505363;
  /* System/Medium/m-medium */
  font-size: 14px;
  font-weight: 500;
  line-height: 100%; /* 14px */
`;
